import { Button } from "@/components/ui/button";
import { ShieldCheck, Award, ArrowRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

const TrustBadge = ({ icon: Icon, label, testId }) => (
  <div
    data-testid={testId}
    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur border border-bee-teal/15 text-xs font-semibold text-bee-teal badge-glow"
  >
    <Icon className="h-3.5 w-3.5" />
    {label}
  </div>
);

const TrustStat = ({ value, label, testId }) => (
  <div className="text-center" data-testid={testId}>
    <div className="font-display text-2xl sm:text-3xl font-bold text-bee-teal">{value}</div>
    <div className="text-[11px] sm:text-xs uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
  </div>
);

export default function Hero() {
  const navigate = useNavigate();
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="home" className="relative overflow-hidden pt-[72px]" data-testid="hero-section">
      {/* Decorative background */}
      <div className="absolute inset-0 bee-grid-bg opacity-50 dark:opacity-30 pointer-events-none" />
      <div className="absolute -top-32 -right-24 h-[420px] w-[420px] rounded-full bg-bee-honey/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-24 h-[420px] w-[420px] rounded-full bg-bee-teal/15 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-bee-teal/10 border border-bee-teal/20 text-xs font-semibold text-bee-teal mb-6" data-testid="hero-eyebrow">
              <Sparkles className="h-3.5 w-3.5" />
              ISO Certified · MSME Certified · Pan-India & Global
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02] text-foreground">
              Your trusted partner in{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-bee-teal">recruitment</span>
                <span className="absolute -bottom-1 left-0 right-0 h-3 bg-bee-honey/40 -rotate-1 z-0" />
              </span>{" "}
              &amp; IT solutions.
            </h1>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              We help companies hire faster and smarter — from single niche roles to 1,000+ bulk hiring drives.
              Backed by AI-driven screening and a Pan-India recruiter network.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                size="lg"
                onClick={() => scrollTo("contact")}
                data-testid="hero-cta-hire"
                className="bg-bee-teal text-white hover:bg-[hsl(var(--bee-teal-dark))] rounded-full px-7 h-12"
              >
                Hire Talent
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate("/jobs")}
                data-testid="hero-cta-jobs"
                className="rounded-full px-7 h-12 border-bee-teal/30 text-bee-teal hover:bg-bee-teal hover:text-white"
              >
                Find a Job
              </Button>
            </div>

            <div className="mt-6 flex flex-wrap gap-2.5">
              <TrustBadge icon={ShieldCheck} label="ISO Certified" testId="hero-badge-iso" />
              <TrustBadge icon={Award} label="MSME (Udyam)" testId="hero-badge-msme" />
            </div>
          </div>

          {/* Right visual */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-bee-teal/15 shadow-2xl shadow-bee-teal/10">
              <img
                src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=900&auto=format&fit=crop&q=80"
                alt="Team collaboration at WorkingBees"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-bee-teal/70 via-bee-teal/10 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="text-xs uppercase tracking-[0.2em] text-white/80">Live Hiring</div>
                <div className="font-display text-2xl font-bold mt-1">500+ candidates placed this year</div>
              </div>
            </div>
            {/* Floating card */}
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-card border border-border rounded-2xl shadow-xl p-4 max-w-[220px]" data-testid="hero-floating-card">
              <div className="text-xs text-muted-foreground">Average time to fill</div>
              <div className="font-display text-2xl font-bold text-bee-teal">&lt; 10 days</div>
              <div className="mt-2 flex -space-x-2">
                <div className="h-7 w-7 rounded-full bg-bee-teal/80 border-2 border-card" />
                <div className="h-7 w-7 rounded-full bg-bee-orange/80 border-2 border-card" />
                <div className="h-7 w-7 rounded-full bg-bee-honey/90 border-2 border-card" />
                <div className="h-7 w-7 rounded-full bg-secondary border-2 border-card text-[10px] text-foreground flex items-center justify-center font-bold">+50</div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust stat strip */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 py-6 px-6 rounded-2xl bg-card border border-border" data-testid="hero-stats-strip">
          <TrustStat value="500+" label="Candidates Placed" testId="hero-stat-1" />
          <TrustStat value="100+" label="Clients" testId="hero-stat-2" />
          <TrustStat value="5+" label="Years Experience" testId="hero-stat-3" />
          <TrustStat value="Pan-India" label="Presence" testId="hero-stat-4" />
        </div>
      </div>
    </section>
  );
}
